/**
 * Resumo do Mês (Portal do Condômino, 0.1.33). Mobile-first.
 *
 * O morador abre este resumo pelo botão "Resumo do mês" do Portal. No celular a
 * tela é só para VER: mostra a imagem amigável do resumo (sem baixar nada) e
 * deixa TROCAR o mês (lista só os meses já processados). O envio no WhatsApp
 * continua sendo feito no micro (tela do Dashboard).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, resumoPortalImgUrl } from "../lib/api";

interface MesResumo { competencia_id: number; periodo: string }
interface ResumoPortal {
  tem: boolean;
  competencia_id?: number;
  periodo?: string;
  condominio?: string;
  meses: MesResumo[];
}

export function ResumoPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  // Mês escolhido (null = deixa o backend usar o mais recente).
  const [mesSel, setMesSel] = useState<number | null>(null);

  const { data, isLoading } = useQuery<ResumoPortal>({
    queryKey: ["resumo-portal", condominioId, mesSel],
    queryFn: () =>
      api.get(
        `/condominios/${condominioId}/resumo-portal` +
          (mesSel ? `?competencia_id=${mesSel}` : ""),
      ),
  });

  const compId = data?.competencia_id ?? null;

  return (
    <div className="max-w-lg mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar} title="Voltar ao Portal"
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Resumo do mês</div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
          <Loader2 size={18} className="animate-spin" /> Carregando o resumo…
        </div>
      )}

      {!isLoading && data && !data.tem && (
        <div className="mt-4 p-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          Ainda não há um mês processado para resumir. Assim que o síndico lançar o
          balancete, o resumo aparece aqui.
        </div>
      )}

      {!isLoading && data?.tem && (
        <div className="space-y-4">
          {/* Trocar o mês (só meses já processados) */}
          {data.meses.length > 1 && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 flex-shrink-0">Mês:</label>
              <select
                value={data.competencia_id}
                onChange={(e) => setMesSel(Number(e.target.value))}
                className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 bg-white"
              >
                {data.meses.map((m) => (
                  <option key={m.competencia_id} value={m.competencia_id}>{m.periodo}</option>
                ))}
              </select>
            </div>
          )}

          {/* Imagem amigável do resumo (não baixa nada) */}
          {compId && (
            <img
              src={resumoPortalImgUrl(condominioId, compId)}
              alt={`Resumo de ${data.periodo}`}
              className="w-full rounded-xl border border-slate-200 shadow-sm bg-white"
            />
          )}
        </div>
      )}
    </div>
  );
}
